import React, { useEffect, useState, useCallback } from 'react';
import { UserPlus, ShieldCheck, ShieldOff, Trash2, Plus, Users, FileText, MessageSquare,
  RefreshCw, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  addInvestorDoc, addTestimonial, approveInvestor, deleteInvestorDoc, deleteTestimonial,
  getInvestorAudit, getInvestorContent, getInvestorKpis, listInvestorDocs, listInvestors,
  listTestimonials, revokeInvestor, setInvestorContentSection, setInvestorKpis, updateTestimonial,
} from './api';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const KPI_FIELDS = [
  ['live_deployments', 'Live Deployments'],
  ['active_trials', 'Active Trials'],
  ['paying_customers', 'Paying Customers'],
  ['mrr', 'MRR'],
  ['arr', 'ARR'],
  ['qualified_pipeline', 'Qualified Pipeline'],
  ['trial_conversion', 'Trial → Paid'],
  ['countries', 'Countries / Markets'],
];

const CONTENT_SECTIONS = [
  ['investment-case', 'Investment Case'],
  ['traction', 'Traction'],
  ['products', 'Products'],
  ['market', 'Market'],
  ['business-model', 'Business Model'],
  ['growth', 'Growth'],
  ['international', 'International'],
  ['financials', 'Financials'],
  ['roadmap', 'Roadmap'],
];

const PRODUCT_OPTIONS = [
  { value: 'videra-healthcare', label: 'Videra Healthcare' },
  { value: 'videra-hard-services', label: 'Videra Hard Services' },
  { value: 'videra-construction', label: 'Videra Construction' },
  { value: 'videra-pm', label: 'Videra PM' },
  { value: 'videra-vanguard', label: 'Videra Vanguard' },
  { value: 'handback-readiness', label: 'Handback Readiness' },
  { value: 'safetrace', label: 'SafeTrace' },
  { value: 'firestopping-business-dashboard', label: 'Firestopping Business Dashboard' },
  { value: 'keystone-consultancy', label: 'Keystone Consultancy' },
];

const InvestorAdmin = () => {
  const [investors, setInvestors] = useState([]);
  const [docs, setDocs] = useState([]);
  const [kpis, setKpis] = useState({});
  const [content, setContent] = useState({});
  const [audit, setAudit] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tempPasswords, setTempPasswords] = useState({});
  const [contentEditing, setContentEditing] = useState({});

  const load = useCallback(async () => {
    try {
      const [i, d, k, c, a, t] = await Promise.all([
        listInvestors(), listInvestorDocs(), getInvestorKpis(),
        getInvestorContent(), getInvestorAudit(), listTestimonials(),
      ]);
      setInvestors(i); setDocs(d); setKpis(k || {}); setContent(c || {});
      setAudit(a); setTestimonials(t);
    } catch (err) { toast.error('Load failed', { description: err.message }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    try {
      const res = await approveInvestor(id);
      setTempPasswords((prev) => ({ ...prev, [id]: res.temporary_password }));
      toast.success('Approved', { description: 'Temporary password shown below. Share it securely.' });
      load();
    } catch (err) { toast.error('Approve failed', { description: err.message }); }
  };
  const handleRevoke = async (id) => {
    try { await revokeInvestor(id); toast.success('Access revoked'); load(); }
    catch (err) { toast.error('Revoke failed', { description: err.message }); }
  };
  const handleAddDoc = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await addInvestorDoc({
        category: f.get('category'), title: f.get('title'),
        url: f.get('url'), description: f.get('description') || '',
      });
      e.target.reset();
      toast.success('Document added'); load();
    } catch (err) { toast.error('Add failed', { description: err.message }); }
  };
  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Delete this document link?')) return;
    try { await deleteInvestorDoc(id); load(); } catch (err) { toast.error(err.message); }
  };
  const handleKpiSubmit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const payload = Object.fromEntries(KPI_FIELDS.map(([k]) => [k, f.get(k) || null]));
    try { await setInvestorKpis(payload); toast.success('KPIs saved'); load(); }
    catch (err) { toast.error(err.message); }
  };
  const handleContentSave = async (section, title, body) => {
    try {
      await setInvestorContentSection(section, { title, body });
      toast.success(`Saved: ${section}`);
      setContentEditing((prev) => ({ ...prev, [section]: false }));
      load();
    } catch (err) { toast.error(err.message); }
  };
  const handleAddTestimonial = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await addTestimonial({
        product: f.get('product'), quote: f.get('quote'),
        attribution: f.get('attribution'), org: f.get('org') || '',
        order: Number(f.get('order') || 0), published: f.get('published') === 'on',
      });
      e.target.reset();
      toast.success('Testimonial added'); load();
    } catch (err) { toast.error(err.message); }
  };
  const handleToggleTestimonial = async (t) => {
    try { await updateTestimonial(t.id, { ...t, published: !t.published }); load(); }
    catch (err) { toast.error(err.message); }
  };
  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete testimonial?')) return;
    try { await deleteTestimonial(id); load(); } catch (err) { toast.error(err.message); }
  };
  const copyPw = (pw) => { navigator.clipboard.writeText(pw); toast.success('Copied'); };

  return (
    <div className="space-y-12 mt-12" data-testid="admin-investor-panel">
      {/* INVESTORS */}
      <section data-testid="admin-investors-list">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /> Investors</h2>
          <button onClick={load} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
        <div className="border border-slate-800 rounded-lg divide-y divide-slate-900">
          {investors.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No requests yet.</div>}
          {investors.map((inv) => (
            <div key={inv.id} className="p-4" data-testid={`investor-row-${inv.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">{inv.full_name} <span className="text-slate-500 font-normal">· {inv.company}</span></p>
                  <p className="text-xs text-slate-400 mt-0.5">{inv.email} · {inv.investor_type} · {inv.country || '—'}</p>
                  {inv.message && <p className="mt-2 text-xs text-slate-500 max-w-2xl leading-relaxed">{inv.message}</p>}
                  <p className="text-[10px] text-slate-600 mt-1">Requested {fmt(inv.created_at)} · Last login {fmt(inv.last_login)}</p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-1 rounded font-medium ${
                    inv.status === 'approved' ? 'bg-emerald-500/10 text-emerald-300' :
                    inv.status === 'revoked' ? 'bg-red-500/10 text-red-300' : 'bg-amber-500/10 text-amber-300'}`}>{inv.status}</span>
                  {inv.status !== 'approved' && (
                    <button onClick={() => handleApprove(inv.id)} className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-2.5 py-1 rounded" data-testid={`approve-${inv.id}`}>
                      <ShieldCheck className="w-3 h-3" /> Approve
                    </button>
                  )}
                  {inv.status === 'approved' && (
                    <button onClick={() => handleRevoke(inv.id)} className="inline-flex items-center gap-1 border border-slate-700 hover:border-red-500 hover:text-red-300 px-2.5 py-1 rounded text-slate-300" data-testid={`revoke-${inv.id}`}>
                      <ShieldOff className="w-3 h-3" /> Revoke
                    </button>
                  )}
                </div>
              </div>
              {tempPasswords[inv.id] && (
                <div className="mt-3 p-3 rounded bg-blue-500/10 border border-blue-500/30 text-xs">
                  <p className="text-blue-300 font-medium">Temporary password (shown once, copy and share securely):</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="bg-slate-900 px-2 py-1 rounded text-slate-100 flex-1">{tempPasswords[inv.id]}</code>
                    <button onClick={() => copyPw(tempPasswords[inv.id])} className="p-1.5 hover:bg-slate-800 rounded" data-testid={`copy-pw-${inv.id}`}><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* KPIs */}
      <section data-testid="admin-investor-kpis">
        <h2 className="text-lg font-medium mb-4">Investor KPIs</h2>
        <form onSubmit={handleKpiSubmit} className="border border-slate-800 rounded-lg p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {KPI_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">{label}</label>
              <input name={k} defaultValue={kpis[k] || ''} placeholder='e.g. "1" or "Data available to approved investors"' className="w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid={`kpi-${k}`} />
            </div>
          ))}
          <div className="md:col-span-2">
            <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm">Save KPIs</button>
          </div>
        </form>
      </section>

      {/* CONTENT sections */}
      <section data-testid="admin-investor-content">
        <h2 className="text-lg font-medium mb-4">Portal content</h2>
        <div className="space-y-3">
          {CONTENT_SECTIONS.map(([key, label]) => {
            const c = content[key] || { title: '', body: '' };
            const editing = contentEditing[key];
            return (
              <div key={key} className="border border-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-white">{label}</p>
                  <button onClick={() => setContentEditing((p) => ({ ...p, [key]: !editing }))} className="text-xs text-blue-400 hover:text-blue-300" data-testid={`edit-content-${key}`}>{editing ? 'Cancel' : (c.body ? 'Edit' : 'Add')}</button>
                </div>
                {editing ? (
                  <ContentEditor initial={c} onSave={(title, body) => handleContentSave(key, title, body)} />
                ) : (
                  <p className="mt-2 text-xs text-slate-500 line-clamp-2">{c.body ? `${c.title}, ${c.body.slice(0, 160)}${c.body.length > 160 ? '…' : ''}` : 'Empty'}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* DOCUMENTS */}
      <section data-testid="admin-investor-docs">
        <h2 className="text-lg font-medium mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" /> Data-room documents</h2>
        <form onSubmit={handleAddDoc} className="border border-slate-800 rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <input name="category" required placeholder="Category (e.g. CORPORATE, FINANCIAL, PRODUCT)" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="doc-category" />
          <input name="title" required placeholder="Title" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="doc-title" />
          <input name="url" required placeholder="URL (Drive / private link)" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100 md:col-span-2" data-testid="doc-url" />
          <input name="description" placeholder="Short description" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100 md:col-span-3" data-testid="doc-description" />
          <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-1 justify-center" data-testid="doc-add"><Plus className="w-3.5 h-3.5" /> Add</button>
        </form>
        <div className="border border-slate-800 rounded-lg divide-y divide-slate-900">
          {docs.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No documents yet.</div>}
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{d.category}</p>
                <p className="text-sm text-slate-200 truncate">{d.title}</p>
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                  {d.url.slice(0, 60)}… <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <button onClick={() => handleDeleteDoc(d.id)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid={`delete-doc-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </section>

      {/* AUDIT */}
      <section data-testid="admin-investor-audit">
        <h2 className="text-lg font-medium mb-4">Access audit</h2>
        <div className="border border-slate-800 rounded-lg divide-y divide-slate-900 max-h-72 overflow-auto">
          {audit.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No events yet.</div>}
          {audit.map((a, i) => (
            <div key={i} className="flex items-center justify-between gap-4 px-4 py-2 text-xs">
              <span className="font-mono text-slate-400">{fmt(a.ts)}</span>
              <span className="text-slate-300 flex-1">{a.action}</span>
              <span className="text-slate-500 truncate max-w-xs">{a.resource || '—'}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="admin-testimonials">
        <h2 className="text-lg font-medium mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-400" /> Testimonials</h2>
        <form onSubmit={handleAddTestimonial} className="border border-slate-800 rounded-lg p-4 space-y-2 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <select name="product" required className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="testimonial-product">
              {PRODUCT_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <input name="order" type="number" defaultValue={0} placeholder="Order" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="testimonial-order" />
          </div>
          <textarea name="quote" required rows={3} placeholder="Quote (10-800 chars)" className="w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="testimonial-quote" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input name="attribution" required placeholder="Attribution (e.g. J. Smith)" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="testimonial-attribution" />
            <input name="org" placeholder="Organisation" className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" data-testid="testimonial-org" />
          </div>
          <label className="text-xs text-slate-400 flex items-center gap-2">
            <input type="checkbox" name="published" defaultChecked /> Publish immediately
          </label>
          <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm">Add testimonial</button>
        </form>
        <div className="border border-slate-800 rounded-lg divide-y divide-slate-900">
          {testimonials.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No testimonials yet. Add quotes only when approved by attribution party.</div>}
          {testimonials.map((t) => (
            <div key={t.id} className="p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-blue-400 font-semibold">{t.product} {t.published ? '' : '· UNPUBLISHED'}</p>
                <p className="text-sm text-slate-200 mt-1 leading-snug">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-xs text-slate-500 mt-1">— {t.attribution} {t.org && `· ${t.org}`}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleToggleTestimonial(t)} className="text-xs px-2 py-1 border border-slate-700 rounded hover:border-blue-500 text-slate-300" data-testid={`toggle-${t.id}`}>
                  {t.published ? 'Unpublish' : 'Publish'}
                </button>
                <button onClick={() => handleDeleteTestimonial(t.id)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid={`delete-testimonial-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const ContentEditor = ({ initial, onSave }) => {
  const [title, setTitle] = useState(initial.title || '');
  const [body, setBody] = useState(initial.body || '');
  return (
    <div className="mt-3 space-y-2">
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Section title" className="w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Section body (plain text, line-breaks preserved)" className="w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100" />
      <button onClick={() => onSave(title, body)} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm">Save</button>
    </div>
  );
};

export default InvestorAdmin;
